import { useState, useEffect } from 'react';
import { Package, RefreshCw } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { formatBytes } from '../lib/utils';
import type { ApiService } from '../lib/api';

interface FirmwarePanelProps {
  api: ApiService | null;
  onSelect?: (version: string) => void;
}

export const FirmwarePanel = ({ api, onSelect }: FirmwarePanelProps) => {
  const [firmware, setFirmware] = useState<Record<string, { size?: number; modified?: string }>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFirmware = async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getFirmwareList();
      setFirmware(data || {});
    } catch (e) {
      setError((e as Error).message);
    }
    setLoading(false);
  };

  useEffect(() => { loadFirmware(); }, [api]);

  const firmwareEntries = Object.entries(firmware).sort((a, b) => {
    const dateA = new Date(a[1].modified || 0);
    const dateB = new Date(b[1].modified || 0);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <Card title="Available Firmware" icon={Package} action={<Button size="sm" variant="ghost" onClick={loadFirmware}><RefreshCw size={14} /></Button>}>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-4"><RefreshCw className="animate-spin" size={16} /> Loading...</div>
      ) : error ? (
        <p className="text-red-400 text-sm">{error}</p>
      ) : firmwareEntries.length === 0 ? (
        <p className="text-gray-500 text-sm">No firmware available</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-auto">
          {firmwareEntries.map(([version, info]) => (
            <div key={version} className="p-2 bg-gray-700 rounded text-sm">
              <div className="flex items-center justify-between">
                <span className="text-white font-mono">{version}</span>
                {onSelect && <Button size="sm" variant="ghost" onClick={() => onSelect(version)}>Select</Button>}
              </div>
              <div className="flex gap-3 mt-1 text-xs text-gray-400">
                <span>{formatBytes(info.size)}</span>
                <span>{info.modified ? new Date(info.modified).toLocaleDateString() : 'Unknown date'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
